import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { AuthState } from '../../store/auth/auth.reducer';
import * as AuthSelectors from '../../store/auth/auth.selectors';
import { PlatformService } from './platform.service';

// The whole auth slice minus transient fields, so that a restored session is
// indistinguishable from a reloaded one: guards and pages read user, domains
// and roles without refetching. No password is ever stored, only these.
export type PersistedAuth = Pick<AuthState,
  'currentUser' | 'availablesDomains' | 'currentDomain' |
  'currentDomainProperties' | 'userRoles' | 'jwtToken'>;

const STORAGE_KEY = 'auth';
const INSTALL_KEY = 'uh.install';

@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly store = inject(Store);
  private readonly platformService = inject(PlatformService);

  // Failures are logged and swallowed throughout: a keychain that misbehaves
  // must mean "not logged in", never a broken app.
  public async load(): Promise<PersistedAuth | null> {
    if (!this.platformService.isNative) {
      return null;
    }

    try {
      if (this.platformService.platform === 'ios') {
        // The token must not travel to the user's other devices via iCloud.
        await SecureStorage.setSynchronize(false);
      }

      if (this.isFreshInstall()) {
        await this.clear();
        return null;
      }

      const stored = await SecureStorage.get(STORAGE_KEY);

      return typeof stored === 'string' ? JSON.parse(stored) as PersistedAuth : null;
    } catch (error) {
      console.warn('Secure storage unavailable, starting signed out.', error);
      return null;
    }
  }

  // iOS keeps Keychain entries when the app is deleted, so a reinstall would
  // come back already signed in as whoever used the phone last. Web storage does
  // go with the app, so a missing flag means this install has never run and
  // whatever the Keychain kept belongs to the previous one.
  private isFreshInstall(): boolean {
    try {
      if (localStorage.getItem(INSTALL_KEY)) {
        return false;
      }

      localStorage.setItem(INSTALL_KEY, new Date().toISOString());
      return true;
    } catch (error) {
      // Storage that cannot be read would otherwise sign the user out at every
      // launch, which is worse than the case this guards against.
      console.warn('Could not read the install flag, keeping the stored session.', error);
      return false;
    }
  }

  public async clear(): Promise<void> {
    if (!this.platformService.isNative) {
      return;
    }

    try {
      await SecureStorage.remove(STORAGE_KEY);
    } catch (error) {
      console.warn('Could not clear secure storage.', error);
    }
  }

  // One subscription instead of an effect per action: this covers login, token
  // refresh, domain change, roles, profile edits and logout alike.
  public startPersisting(): void {
    if (!this.platformService.isNative) {
      return;
    }

    this.store.select(AuthSelectors.getAuthState).pipe(
      map((state): PersistedAuth => ({
        currentUser: state.currentUser,
        availablesDomains: state.availablesDomains,
        currentDomain: state.currentDomain,
        currentDomainProperties: state.currentDomainProperties,
        userRoles: state.userRoles,
        jwtToken: state.jwtToken
      })),
      distinctUntilChanged((previous, current) =>
        previous.currentUser === current.currentUser &&
        previous.availablesDomains === current.availablesDomains &&
        previous.currentDomain === current.currentDomain &&
        previous.currentDomainProperties === current.currentDomainProperties &&
        previous.userRoles === current.userRoles &&
        previous.jwtToken === current.jwtToken),
      debounceTime(250)
    ).subscribe(auth => {
      if (!auth.jwtToken) {
        void this.clear();
        return;
      }

      void this.save(auth);
    });
  }

  private async save(auth: PersistedAuth): Promise<void> {
    try {
      await SecureStorage.set(STORAGE_KEY, JSON.stringify(auth));
    } catch (error) {
      console.warn('Could not write to secure storage.', error);
    }
  }
}
